import { handleStoreState } from "Stores/RESTStore/CoreStore";
import { XKTViewerStore } from "./XKTViewerStore";
import { action, computed, makeObservable, observable, runInAction, toJS } from "mobx";
import { API } from "General/API";
import { projectsDataStore } from "Stores";
import { DefaultOptionType } from "antd/lib/select";
import dayjs, { Dayjs } from "dayjs";
import { API_URL } from "General/APIEndpoints";
import { contactsStore } from "Stores/ContactsStore";
import { isEmpty, uniqBy } from "lodash";
import i18n, { I18N_NS } from "Config/i18n";

export interface BcfServerTopicWithModelIdAndDeleted extends API_BcfServerTopic {
  modelId: number;
  deleted: boolean;
}
export interface TopicFilterParams {
  topic_type?: string;
  topic_status?: string;
  priority?: string;
  creation_author?: string;
  creation_date?: Dayjs;
  assigned_to?: Dayjs;
}

export interface SubmitTopicRequest extends API_BcfServerTopic {
  viewpoints?: API_BcfServerViewpoint[];
}

export class BCFStore {
  state: StoreState = "RAW";
  topics: BcfServerTopicWithModelIdAndDeleted[] = [];
  showDeletedTopics: boolean = false;
  extensions: API_BcfServerProjectExtensions = {};
  projectUsers: DefaultOptionType[] = [];
  topicFilterParams: TopicFilterParams = {};
  // used when adding a viewpoint. Create Issue form gets hidden, then shown again.
  // JSON string is used to consume less memory and still be an observable
  preservedFormData: string = "";
  /**
   * Full viewpoints for create-issue submit. The Ant Design form only holds a light
   * preview (guid + snapshot); putting `components` (~MBs) into setFieldsValue blocks
   * the main thread for tens of seconds and delays the modal opening.
   */
  private draftViewpoints: API_BcfServerViewpoint[] = [];
  showAllIssues: boolean = false;
  private readonly context: XKTViewerStore;

  constructor(context: XKTViewerStore) {
    this.context = context;
    makeObservable(this, {
      // observables
      state: observable,
      projectUsers: observable,
      extensions: observable,
      topics: observable,
      topicFilterParams: observable,
      showDeletedTopics: observable,
      showAllIssues: observable,
      // computed
      stageOptions: computed,
      labelOptions: computed,
      assignedToOptions: computed,
      // actions
      clear: action.bound,
      init: action.bound,
      fetchAllTopics: action.bound,
      fetchProjectExtensions: action.bound,
      fetchTopicsOfFile: action.bound,
      changeShowDeletedTopics: action.bound,
      toggleShowAllIssues: action.bound,
      updateDisplayOptions: action.bound,
      filterTopics: action.bound,
    });
  }

  clear() {
    this.state = "RAW";
    this.extensions = {};
    this.projectUsers = [];
    this.topicFilterParams = {};
    this.topics = [];
    this.preservedFormData = "";
    this.draftViewpoints = [];
    this.showDeletedTopics = false;
    this.showAllIssues = false;
  }

  async init() {
    await Promise.all([this.fetchAllTopics(), this.fetchProjectExtensions()]);
  }

  async fetchTopicsOfFile(fileId: number): Promise<BcfServerTopicWithModelIdAndDeleted[]> {
    const queryParams: { $filter?: string; $orderby?: string; showDeleted?: boolean } = {
      showDeleted: this.showDeletedTopics,
    };

    const response = await API.getData<API_BcfServerTopicAndDeleted[]>(
      API_URL.BCF_SERVER_EXTENSIONS.bcfServerGetTopicsByFileId(projectsDataStore.projectId, fileId),
      queryParams
    );
    return response.map((item) => ({ ...item.topic, modelId: fileId, deleted: item.deleted }));
  }

  async fetchAllProjectTopics(): Promise<BcfServerTopicWithModelIdAndDeleted[]> {
    const queryParams: { withDeleted?: boolean } = {
      withDeleted: this.showDeletedTopics,
    };
    const response = await API.getData<API_BcfServerTopicWithIfcProjects[]>(
      API_URL.BCF_SERVER_EXTENSIONS.bcfServerGetAllTopicsWithIfcProjects(projectsDataStore.projectId),
      queryParams
    );
    const loadedModelIds = new Set(this.context.models.map((m) => m.fileId));

    return response.map((item) => {
      const modelId = this.resolveModelIdFromIfcProjects(item.ifcProjects, loadedModelIds);

      return {
        ...item.bcfServerTopic,
        modelId,
        deleted: item.deleted,
      };
    });
  }

  /**
   * Resolves the best model ID from ifcProjects list.
   * Prefers a model that is currently loaded in the viewer, otherwise falls back to the first entry.
   */
  private resolveModelIdFromIfcProjects(ifcProjects: string[] | undefined, loadedModelIds: Set<number>): number | null {
    if (!ifcProjects || ifcProjects.length === 0) {
      return null;
    }

    const parsedIds = ifcProjects.map((id) => Number.parseInt(id, 10)).filter((id) => !Number.isNaN(id));

    if (parsedIds.length === 0) {
      return null;
    }

    const loadedMatch = parsedIds.find((id) => loadedModelIds.has(id));
    return loadedMatch ?? parsedIds[0];
  }

  @handleStoreState()
  async fetchAllTopics() {
    this.context.sdk.sendMessage({ type: "setBcfSidebarLoading", payload: { loading: true } });
    const modelIds = this.context.models.map((model) => model.fileId);
    let newTopics: BcfServerTopicWithModelIdAndDeleted[] = null;
    if (this.showAllIssues) {
      // When showing all issues:
      // 1. First fetch topics from currently open models (will have correct modelId)
      // 2. Then fetch all project topics and merge them (add topics not in current models)
      const currentModelTopics = await Promise.all(modelIds.map((id) => this.fetchTopicsOfFile(id)));
      const currentModelTopicsFlat = [].concat(...currentModelTopics);

      const allProjectTopics = await this.fetchAllProjectTopics();

      // Merge: keep current model topics (they have correct modelId), add others
      const mergedTopics = [...currentModelTopicsFlat];
      const currentTopicGuids = new Set(currentModelTopicsFlat.map((t) => t.guid));

      // Add topics that are not in currently open models
      // Enrich them with authorization data including status options from extensions
      allProjectTopics.forEach((topic) => {
        if (!currentTopicGuids.has(topic.guid)) {
          const needsStatusEnrichment =
            !topic.authorization || !topic.authorization.topic_status || topic.authorization.topic_status.length === 0;
          if (needsStatusEnrichment) {
            if (!topic.authorization) {
              topic.authorization = {};
            }
            if (this.extensions?.topic_status && this.extensions.topic_status.length > 0) {
              topic.authorization.topic_status = [...this.extensions.topic_status];
            }
          }
          mergedTopics.push(topic);
        }
      });

      newTopics = mergedTopics;
    } else {
      const result = await Promise.all(modelIds.map((id) => this.fetchTopicsOfFile(id)));
      newTopics = uniqBy([].concat(...result), "guid");
    }
    const topicsWithAssignedTo = this.mapTopicsWithAssignedTo(newTopics);
    runInAction(() => {
      this.topics = topicsWithAssignedTo;
    });
    this.context.sdk.sendMessage({ type: "setBcfSidebarLoading", payload: { loading: false } });
    this.context.sdk.sendMessage({
      type: "setBcfIssuesFilterParams",
      payload: { isDirty: !isEmpty(this.topicFilterParams), showAllIssues: this.showAllIssues },
    });
    return this.topics.slice();
  }

  async fetchProjectExtensions() {
    const response = await API.getData<API_BcfServerProjectExtensions>(
      API_URL.BCF_SERVER_PROJECTS.bcfServerGetProjectExtensions(projectsDataStore.bcfGuid)
    );
    runInAction(() => {
      this.extensions = response;
      this.projectUsers = this.buildProjectUsers(response);
    });
  }

  private buildProjectUsers(response: API_BcfServerProjectExtensions): DefaultOptionType[] {
    if (response.userIds?.length) {
      return response.userIds.map((userId) => ({
        label: contactsStore.getUserNameById(userId) || i18n.t("[Removed user]", { ns: I18N_NS.GENERAL }),
        value: userId,
      }));
    }
    if (response.topicAssignments?.length) {
      return response.topicAssignments
        .filter((item) => item.id != null)
        .map((item) => ({
          label: contactsStore.getUserNameById(item.id, false) || item.name,
          value: item.id,
        }));
    }
    return contactsStore.users.map((user) => ({
      label: contactsStore.getUserNameById(user.id) || i18n.t("[Removed user]", { ns: I18N_NS.GENERAL }),
      value: user.id,
    }));
  }

  async refreshTopics() {
    const topics = await this.fetchAllTopics();
    const filteredTopics = this.getFilteredTopics(topics);
    this.context.sdk.sendMessage({
      type: "setBcfIssuesResponse",
      payload: { data: JSON.parse(JSON.stringify(filteredTopics)) },
    });
  }

  async fetchViewpointsOfTopic(topicId: string): Promise<API_BcfServerViewpoint[]> {
    return await API.getData<API_BcfServerViewpoint[]>(
      API_URL.BCF_SERVER_VIEWPOINTS.bcfServerGetViewpoints(projectsDataStore.bcfGuid, topicId),
      { includeAuthorization: true }
    );
  }

  async fetchViewpointSnapshot(topicId: string, viewpointId: string): Promise<ArrayBuffer> {
    const response: File = await API.getData(
      API_URL.BCF_SERVER_VIEWPOINTS.bcfServerGetViewpointSnapshot(projectsDataStore.bcfGuid, topicId, viewpointId),
      null,
      { responseType: "blob" }
    );
    return response.arrayBuffer();
  }

  async updateTopic(topicId: string, topic: API_BcfServerTopic) {
    await API.editData<API_BcfServerTopic>(
      API_URL.BCF_SERVER_TOPICS.bcfServerPutTopicById(projectsDataStore.bcfGuid, topicId),
      topic
    );
    runInAction(() => {
      this.topics = [];
      this.refreshTopics();
    });
  }

  async submitIssue({ viewpoints, ...topic }: SubmitTopicRequest) {
    const topicResponse = await API.newData<API_BcfServerTopic>(
      API_URL.BCF_SERVER_TOPICS.bcfServerPostTopic(projectsDataStore.bcfGuid),
      topic
    );
    await Promise.all(
      this.context.models.map((item) =>
        API.newData<API_BcfServerIfcFileInfo[]>(
          API_URL.BCF_SERVER_EXTENSIONS.bcfServerAddIfcModelToTopic(
            projectsDataStore.projectId,
            topicResponse.guid,
            item.fileId
          ),
          {}
        )
      )
    );
    const viewpointsToSubmit = this.resolveViewpointsForSubmit(viewpoints);
    if (viewpointsToSubmit.length) {
      await Promise.all(
        // extract guid from request because BIM Collab API doesn't accept its format
        viewpointsToSubmit.map(({ guid, ...viewpoint }) =>
          API.newData(
            API_URL.BCF_SERVER_VIEWPOINTS.bcfServerPostViewpoint(projectsDataStore.bcfGuid, topicResponse.guid),
            viewpoint
          )
        )
      );
    }
    this.clearStoredFormData();
    this.refreshTopics();
    return topicResponse;
  }

  /** Form/UI only needs guid + snapshot; keep heavy `components` out of Ant Form state. */
  private toFormViewpointPreview(viewpoint: API_BcfServerViewpoint): API_BcfServerViewpoint {
    return {
      guid: viewpoint.guid,
      snapshot: viewpoint.snapshot,
    };
  }

  private resolveViewpointsForSubmit(formViewpoints?: API_BcfServerViewpoint[]): API_BcfServerViewpoint[] {
    if (!formViewpoints?.length) {
      return [];
    }
    return formViewpoints.map((formViewpoint) => {
      const full = this.draftViewpoints.find((draft) => draft.guid === formViewpoint.guid);
      return full || formViewpoint;
    });
  }

  addViewpointToFormData(viewpoint: API_BcfServerViewpoint) {
    try {
      const formData = JSON.parse(this.preservedFormData || "{}");
      if (!formData.viewpoints) {
        formData.viewpoints = [];
      }
      this.draftViewpoints = [...this.draftViewpoints, viewpoint];
      formData.viewpoints.push(this.toFormViewpointPreview(viewpoint));
      return formData;
    } catch (error) {
      console.warn("BCFStore.addViewpointToFormData: ", error.message);
    }
  }

  storeFormData(payload: string) {
    this.preservedFormData = payload;
  }

  async showViewpointInViewer(
    fileId: number,
    topicGuid: string,
    viewpoint: API_BcfServerViewpoint,
    requestId?: string
  ) {
    let returnedViewpoint: API_BcfServerViewpoint;
    if (viewpoint.components) {
      returnedViewpoint = viewpoint;
    } else {
      // https://github.com/buildingSMART/BCF-API#352-post-viewpoint-service
      // to receive full viewpoint information need to do a few more requests
      // because viewpoint components are splitted into several requests
      const [componentWithSelection, componentWithVisibility, componentColoring] = await Promise.all([
        API.getData<API_BcfServerComponents>(
          API_URL.BCF_SERVER_VIEWPOINTS.bcfServerGetViewpointSelection(
            projectsDataStore.bcfGuid,
            topicGuid,
            viewpoint.guid
          )
        ),
        API.getData<API_BcfServerComponents>(
          API_URL.BCF_SERVER_VIEWPOINTS.bcfServerGetViewpointVisibility(
            projectsDataStore.bcfGuid,
            topicGuid,
            viewpoint.guid
          )
        ),
        API.getData<API_BcfServerComponents>(
          API_URL.BCF_SERVER_VIEWPOINTS.bcfServerGetViewpointColoring(
            projectsDataStore.bcfGuid,
            topicGuid,
            viewpoint.guid
          )
        ),
      ]);
      returnedViewpoint = {
        ...viewpoint,
        components: {
          selection: componentWithSelection.selection,
          visibility: componentWithVisibility.visibility,
          coloring: componentColoring.coloring,
        },
      };
    }
    this.context.setBcfViewpoint(fileId, returnedViewpoint, requestId);
    this.context.sdk.selectBcfIssue(topicGuid);
    return viewpoint;
  }

  filterTopics(filterData) {
    this.topicFilterParams = { ...filterData };
    const topics = this.getFilteredTopics();
    this.context.sdk.sendMessage({
      type: "setBcfIssuesResponse",
      payload: { data: JSON.parse(JSON.stringify(topics)) },
    });
    this.context.sdk.sendMessage({
      type: "setBcfIssuesFilterParams",
      payload: { isDirty: !isEmpty(filterData), showAllIssues: this.showAllIssues },
    });
  }

  clearStoredFormData() {
    this.preservedFormData = "";
    this.draftViewpoints = [];
  }

  changeShowDeletedTopics(value?: boolean) {
    this.showDeletedTopics = value;
  }

  toggleShowAllIssues() {
    this.showAllIssues = !this.showAllIssues;
    this.refreshTopics();
  }

  updateDisplayOptions(options: { showDeleted?: boolean; showAllIssues?: boolean }) {
    if (typeof options.showDeleted === "boolean") {
      this.showDeletedTopics = options.showDeleted;
    }
    if (typeof options.showAllIssues === "boolean") {
      this.showAllIssues = options.showAllIssues;
    }
    this.refreshTopics();
  }

  getFilteredTopics(topics: BcfServerTopicWithModelIdAndDeleted[] = this.topics) {
    let filteredTopics: BcfServerTopicWithModelIdAndDeleted[];
    if (this.filterParamsNotEmpty) {
      const filterPredicates = Object.entries(this.topicFilterParams).reduce((list, [key, value]) => {
        if (value) {
          let predicateFn: (o: API_BcfServerTopic) => boolean;
          if (dayjs.isDayjs(value)) {
            predicateFn = (o) => dayjs(o[key]).isSame(value, "day");
          } else if (key === "assigned_to_id") {
            predicateFn = (o) => {
              if (o.assigned_to === null && o.assigned_to_id === null) {
                return false;
              }
              const valueInt = Number.parseInt(value as string);
              if (Number.isNaN(valueInt)) {
                return o.assigned_to === value;
              } else {
                return Number.parseInt(o.assigned_to_id as unknown as string) === valueInt;
              }
            };
          } else if (key === "labels") {
            predicateFn = (o) => o.labels?.some((filterValue) => value.includes(filterValue));
          } else {
            predicateFn = (o) => o[key] === value;
          }
          list.push(predicateFn);
        }
        return list;
      }, []);
      filteredTopics = topics.filter((item) => filterPredicates.every((fn) => fn(item)));
    } else {
      filteredTopics = topics.slice();
    }
    return this.mapTopicsWithAssignedTo(filteredTopics);
  }

  mapTopicsWithAssignedTo(topics: BcfServerTopicWithModelIdAndDeleted[]) {
    return topics.map((topic) => ({
      ...topic,
      assigned_to: contactsStore.getUserNameById(topic.assigned_to_id, false) || topic.assigned_to,
    }));
  }

  get filterParamsNotEmpty() {
    return !isEmpty(toJS(this.topicFilterParams));
  }

  get stageOptions() {
    if (this.extensions?.stage?.length > 0) {
      return this.extensions.stage.map((item) => ({
        label: item,
        value: item,
      }));
    }
    return [];
  }

  get labelOptions() {
    if (this.extensions?.topic_label?.length > 0) {
      return this.extensions.topic_label.map((item) => ({
        label: item,
        value: item,
      }));
    }
    return [];
  }

  get assignedToOptions() {
    return (this.extensions.topicAssignments || []).map((item, idx) => ({
      key: idx,
      value: item.id ? String(item.id) : item.name,
      label: contactsStore.getUserNameById(Number(item.id), false) || item.name,
    }));
  }
}
